// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable spire/fenced-imports
import Tippy from "@tippyjs/react";

import findProductByErp from "@bg/App/Handlers/FindProductByErp/FindProductByErp";
import { CustomFieldDefinition, ImageMapPickerDefinition } from "@bg/CustomFieldDefinition";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbarHorizontal } from "@bg/XDL/Utilities";
import ShopByLookProduct from "@bgs/App/Widgets/ShopByLook/ShopByLookProduct";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    subText = "subText",
    viewMoreLink = "viewMoreLink",
    slides = "slides",
}

interface Coords {
    xPos: number;
    yPos: number;
    productId: string;
    pointColor: string;
}

interface SlideModel {
    fields: {
        imageUrl: string;
        coords: ImageMapPickerDefinition;
        slideTitle: string;
        slideSubText: string;
        viewMoreLink: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subText]: string;
        [fields.slides]: SlideModel[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const slidesLinks = ownProps.fields.slides.map(slide => {
        if (slide.fields.viewMoreLink) {
            const path = getPageLinkByNodeId(state, slide.fields.viewMoreLink.value);
            return path?.url;
        }
        return "";
    });
    return {
        slidesLinks,
    };
};

const mapDispatchToProps = {
    findProductByErp,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ShopByLookStyles {
    widgetWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    headingContainer?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    viewMoreLink?: LinkPresentationProps;
    subHeading?: TypographyPresentationProps;
    firstSlideWrapper?: InjectableCss;
    secondSlideWrapper?: InjectableCss;
    slideImageContainer?: InjectableCss;
    productPoint?: InjectableCss;
    productPointInner?: InjectableCss;
    secondSliderContainer?: InjectableCss;
    sliderTextContainer?: InjectableCss;
    navigationWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export const styles: ShopByLookStyles = {
    widgetWrapper: {
        css: css`
            height: 830px;
            padding-top: 90px;
            overflow: hidden;
            overflow-x: auto;
            ${styledScrollbarHorizontal(6)}
            @media (max-width: 766px) {
                display: none;
            }
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            height: 700px;
        `,
    },
    headingContainer: {
        css: css`
            position: absolute;
            top: -80px;
            left: 50%;
            transform: translateX(-50%);
            background: ${getColor("common.white")};
            box-shadow: 0 2px 11px 0 rgba(53, 65, 94, 0.25);
            max-width: 898px;
            width: 100%;
            padding: 30px;
            z-index: 2;
            text-align: center;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: flex-end;
            margin-bottom: ${fontConfig.spacing.smallHeading};
        `,
    },
    title: {
        variant: "h2",
        forwardAs: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    viewMoreLink: {
        css: css`
            line-height: 0;
        `,
    },
    firstSlideWrapper: {
        css: css`
            overflow: hidden;
            min-width: 1360px;
        `,
    },
    secondSlideWrapper: {
        css: css`
            overflow: hidden;
            max-width: 430px;
            position: absolute;
            height: 250px;
            bottom: -30px;
            left: 30px;
            background: white;
            box-shadow: 0 2px 11px 0 rgb(53, 65, 94, 0.25);
            transition: all 150ms;

            &&&.collpased {
                height: 30px;
                bottom: 0;
                max-width: 100%;
                left: 0;
                width: 100%;
            }
        `,
    },
    slideImageContainer: {
        css: css`
            position: relative;
            height: 700px;
            min-width: 100%;
            && > img {
                height: 700px;
                min-width: 1360px;
            }
            .tippy-box {
                background: white;
                padding: 20px;
                min-width: 300px;
                max-width: 445px !important;
            }
        `,
    },
    productPoint: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            cursor: pointer;
            top: ${(props: any) => `calc(${props.yPos * 2}px - 10px)`};
            left: ${(props: any) => `calc(${props.xPos * 2}px - 10px)`};
            width: 21px;
            height: 21px;
            background: transparent;
            border: 2px solid;
            border-color: ${(props: any) => props.pointColor};
            box-shadow: 0 2px 11px 0 rgba(53, 65, 94, 0.25);
            border-radius: 50%;
            transition: all 100ms;
            &&&.selected {
                z-index: 88888;
                box-shadow: none;
                background: ${getColor("white")};
                width: 42px;
                height: 42px;
                top: ${(props: any) => `calc(${props.yPos * 2}px - 21px)`};
                left: ${(props: any) => `calc(${props.xPos * 2}px - 21px)`};
                border-color: transparent;
            }
        `,
    },
    productPointInner: {
        css: css`
            width: 13px;
            height: 13px;
            cursor: pointer;
            background: ${(props: any) => props.pointColor};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 100ms;
            &&.selected {
                width: 34px;
                height: 34px;
                background: ${getColor("primary")};
            }
        `,
    },
    secondSliderContainer: {
        css: css`
            display: flex;
            position: relative;
            height: 100%;
        `,
    },
    sliderTextContainer: {
        css: css`
            min-width: 100%;
            padding: 30px;
            padding-bottom: 40px;
        `,
    },
    navigationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            position: absolute;
            left: 30px;
            bottom: 20px;
            transition: all 150ms;
            &&&.collpased {
                bottom: 5px;
                left: 50%;
                transform: translateX(-50%);
            }
            &&.single {
                justify-content: center;
            }
        `,
    },
    prevArrowWrapper: {
        css: css`
            margin-right: 8px;
            line-height: 17px;
        `,
    },
    prevArrowButton: {
        css: css`
            background: transparent !important;
            border: 0;
            padding: 0;
            min-height: auto;
            &:hover {
                background: none;
            }
            &:focus,
            &:active {
                outline: none !important;
                box-shadow: none;
            }
        `,
    },
    iconProps: {
        size: 15,
        css: css`
            svg {
                color: ${getColor("tertiary.main")};
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            margin-left: 8px;
            line-height: 17px;
        `,
    },
    nextArrowButton: {
        css: css`
            background: transparent !important;
            border: 0;
            padding: 0;
            min-height: auto;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none !important;
                box-shadow: none;
            }
        `,
    },
};

const ShopByLook: React.FC<Props> = ({ fields: { title, subText, slides }, id, findProductByErp, slidesLinks }) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [emblaSecondRef, emblaSecond] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    // used to animat the selected point to show an "X" icon after openning the tooltip
    const [selectedProduct, setSelectedProduct] = useState<Coords>({} as Coords);
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    useEffect(() => {
        if (!emblaSecond) {
            return;
        }

        emblaSecond.on("select", () => {
            setSelectedIndex(emblaSecond.selectedScrollSnap());
        });
    }, [emblaSecond]);

    useEffect(() => {
        if (!embla || !slides || slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
            draggable: false,
        });
    }, [embla, slides]);

    useEffect(() => {
        if (!emblaSecond || !slides || slides.length === 0) {
            return;
        }

        emblaSecond.reInit({
            align: "start",
            loop: true,
            draggable: false,
        });
    }, [emblaSecond, slides]);

    const loadProduct = (erpNumber: string) => {
        findProductByErp({ erpNumber });
    };

    const toggleCollapse = () => {
        setCollapsed(!collapsed);
    };

    if (!slides || !slides.length) {
        return null;
    }

    // to make it applicable on mobile devices, make the img container scrollable on the x-axis to maintain
    // the points coordinates as it
    return (
        <StyledWrapper {...styles.widgetWrapper} data-test-selector="ShopByLook">
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.headingContainer}>
                    <StyledWrapper {...styles.titleWrapper}>
                        {title && <Typography {...styles.title}>{title}</Typography>}
                        {slidesLinks && slidesLinks[selectedIndex] && (
                            <Link href={slidesLinks[selectedIndex]} {...styles.viewMoreLink}>
                                <Icon src="ChevronRight" size={30} color="tertiary" />
                            </Link>
                        )}
                    </StyledWrapper>
                    {subText && <StyledWrapper {...styles.subHeading}>{parse(subText, parserOptions)}</StyledWrapper>}
                </StyledWrapper>
                <StyledWrapper ref={emblaRef} {...styles.firstSlideWrapper}>
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        {slides &&
                            slides.map(slide => {
                                return (
                                    <StyledWrapper
                                        key={`shopByLookSlide_${slide.fields.imageUrl}`}
                                        {...styles.slideImageContainer}
                                    >
                                        <StyledWrapper>
                                            <LazyImage
                                                src={slide.fields.imageUrl}
                                                width="100%"
                                                height="700px"
                                                css={css`
                                                    img {
                                                        object-fit: unset;
                                                        aspect-ratio: 1360 / 700;
                                                    }
                                                `}
                                            />
                                            {slide.fields.coords &&
                                                (slide.fields.coords as any).length > 0 &&
                                                (slide.fields.coords as any).map((coord: Coords) => (
                                                    <StyledWrapper key={`${coord.xPos}_${coord.yPos}`}>
                                                        <Tippy
                                                            inlinePositioning={true}
                                                            interactive={true}
                                                            trigger="click"
                                                            placement="left-start"
                                                            onHide={() => setSelectedProduct({} as Coords)}
                                                            onMount={() => setSelectedProduct(coord)}
                                                            offset={[6, -21]}
                                                            content={<ShopByLookProduct productErp={coord.productId} />}
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "flip",
                                                                        options: {
                                                                            fallbackPlacements: ["right", "top"],
                                                                        },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            <StyledWrapper
                                                                key={`${coord.xPos}_${coord.yPos}`}
                                                                onClick={() => loadProduct(coord.productId)}
                                                                {...styles.productPoint}
                                                                className={
                                                                    isEqual(selectedProduct, coord) ? "selected" : ""
                                                                }
                                                                {...({
                                                                    yPos: coord.yPos,
                                                                    xPos: coord.xPos,
                                                                    pointColor: coord.pointColor,
                                                                } as any)}
                                                            >
                                                                <StyledWrapper
                                                                    className={
                                                                        isEqual(selectedProduct, coord)
                                                                            ? "selected"
                                                                            : ""
                                                                    }
                                                                    {...({
                                                                        pointColor: coord.pointColor,
                                                                    } as any)}
                                                                    {...styles.productPointInner}
                                                                >
                                                                    {isEqual(selectedProduct, coord) ? (
                                                                        <Icon src="X" size={26} />
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </Tippy>
                                                    </StyledWrapper>
                                                ))}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    ref={emblaSecondRef}
                    {...styles.secondSlideWrapper}
                    className={collapsed ? "collpased" : ""}
                >
                    <StyledWrapper {...styles.secondSliderContainer}>
                        {slides &&
                            slides.map(slide => {
                                return (
                                    <StyledWrapper
                                        key={`shopByLookSlideText_${slide.fields.imageUrl}`}
                                        {...styles.sliderTextContainer}
                                    >
                                        <Typography variant="h5">{slide.fields.slideTitle}</Typography>
                                        <StyledWrapper
                                            css={css`
                                                & > ${TypographyStyle} {
                                                    font-size: ${fontConfig.fontSize.small}px;
                                                    font-weight: ${fontConfig.weight.regular};
                                                    line-height: ${fontConfig.lineHeight.small};
                                                    margin-bottom: 0;
                                                }
                                            `}
                                        >
                                            {parse(slide.fields.slideSubText, parserOptions)}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                    </StyledWrapper>
                    <StyledWrapper
                        onClick={() => toggleCollapse()}
                        css={css`
                            position: absolute;
                            top: ${collapsed ? "8px" : "5px"};
                            right: 10px;
                            display: flex;
                            align-items: center;
                            &&:hover {
                                cursor: pointer;
                            }
                        `}
                    >
                        {!collapsed && <Icon src="Minus" size={20} />}
                        {collapsed && <Icon src="Plus" size={15} />}
                    </StyledWrapper>
                    <StyledWrapper
                        {...styles.navigationWrapper}
                        className={`${slides && slides.length === 1 ? "single " : ""}${collapsed ? "collpased" : ""}`}
                    >
                        {slides && slides.length > 1 && (
                            <StyledWrapper {...styles.prevArrowWrapper}>
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => {
                                        embla && embla.scrollPrev();
                                        emblaSecond && emblaSecond.canScrollPrev() && emblaSecond.scrollPrev();
                                    }}
                                >
                                    <ButtonIcon {...styles.iconProps} src="ChevronLeft" />
                                </Button>
                            </StyledWrapper>
                        )}
                        {slides && slides.length > 1 && (
                            <Typography
                                size={fontConfig.fontSize.small}
                                weight={fontConfig.weight.regular}
                                lineHeight={fontConfig.lineHeight.singleLine}
                                css={css`
                                    font-style: italic;
                                `}
                            >{`${selectedIndex + 1} of ${slides.length}`}</Typography>
                        )}
                        {slides && slides.length > 1 && (
                            <StyledWrapper {...styles.nextArrowWrapper}>
                                <Button
                                    {...styles.nextArrowButton}
                                    disabled={
                                        embla && !embla.canScrollNext() && emblaSecond && !emblaSecond.canScrollNext()
                                    }
                                    onClick={() => {
                                        embla && embla.scrollNext();
                                        emblaSecond && emblaSecond.canScrollNext() && emblaSecond.scrollNext();
                                    }}
                                >
                                    <ButtonIcon {...styles.iconProps} src="ChevronRight" />
                                </Button>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const slidesTab = {
    displayName: "Slides",
    sortOrder: 0,
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "XNGAGE" as any,
    fieldDefinitions: [
        {
            displayName: "Title",
            name: fields.title,
            editorTemplate: "TextField",
            defaultValue: "Shop By Look",
            fieldType: "General",
            tab: contentTab,
        },
        {
            displayName: "Sub Heading Text",
            name: fields.subText,
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            tab: contentTab,
        },
        {
            name: fields.slides,
            editorTemplate: "ListField",
            tab: slidesTab,
            getDisplay: (item: HasFields) => {
                return item.fields.slideTitle;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "slideTitle",
                    editorTemplate: "TextField",
                    defualtValue: "",
                },
                {
                    displayName: "Link Url",
                    name: "viewMoreLink",
                    editorTemplate: "LinkField",
                    defaultValue: { type: "Url", value: "" },
                    fieldType: "General",
                },
                {
                    name: "slideSubText",
                    editorTemplate: "RichTextField",
                    defualtValue: "",
                },
                {
                    displayName: "Image Path",
                    name: "imageUrl",
                    defaultValue: "",
                    editorTemplate: "ImagePickerField",
                },
                {
                    name: "coords",
                    displayName: "Products Coordinates",
                    editorTemplate: "ImageMapPickerField",
                    imageUrl: (item: HasFields) => item.fields.imageUrl,
                    fieldType: "General",
                    defaultValue: [] as any,
                    isVisible: (item: HasFields) => item.fields.imageUrl !== "",
                },
            ] as any,
        },
    ],
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByLook),
    definition,
};

export default widgetModule;
